import { wsService } from './websocket';

// STUN/TURN 서버 설정
const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ],
};

interface Participant {
  connectionId: string;
  role: 'debater' | 'jury' | 'spectator';
  stance?: 'pro' | 'con';
  userId: string;
}

interface PeerConnection {
  pc: RTCPeerConnection;
  connectionId: string;
  audioElement?: HTMLAudioElement;
  iceCandidateQueue: RTCIceCandidateInit[]; // ICE candidate 버퍼
  remoteDescriptionSet: boolean; // remote description 설정 여부
}

type WebRTCEventType = 
  | 'localStreamReady'
  | 'remoteStreamAdded'
  | 'peerConnected'
  | 'peerDisconnected'
  | 'error';

type WebRTCEventCallback = (data: unknown) => void;

class WebRTCService {
  private localStream: MediaStream | null = null;
  private peerConnections: Map<string, PeerConnection> = new Map();
  private pendingCandidates: Map<string, RTCIceCandidateInit[]> = new Map(); // PeerConnection 생성 전 도착한 candidates
  private myConnectionId: string | null = null;
  private participants: Participant[] = [];
  private isInitialized = false;
  private isMuted = true; // 기본적으로 음소거
  private eventListeners: Map<WebRTCEventType, Set<WebRTCEventCallback>> = new Map();
  private unsubscribers: (() => void)[] = [];

  // 이벤트 리스너 등록
  on(event: WebRTCEventType, callback: WebRTCEventCallback): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
    return () => {
      this.eventListeners.get(event)?.delete(callback);
    };
  }

  private emit(event: WebRTCEventType, data: unknown) {
    this.eventListeners.get(event)?.forEach(cb => cb(data));
  }

  // 초기화 - 마이크 접근 및 WebSocket 이벤트 등록
  async initialize(options?: { audioOnly?: boolean }): Promise<void> {
    if (this.isInitialized) return;

    try {
      // audioOnly가 아니면 마이크 접근 요청 (토론자)
      if (!options?.audioOnly) {
        this.localStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });

        // 기본적으로 음소거 상태로 시작
        this.setMuted(true);

        this.emit('localStreamReady', { stream: this.localStream });
        console.log('[WebRTC] Local stream ready');
      } else {
        console.log('[WebRTC] Audio-only mode (listener)');
      }

      // WebSocket 이벤트 리스너 등록
      this.setupWebSocketListeners();

      this.isInitialized = true;
    } catch (error) {
      console.error('[WebRTC] Failed to get user media:', error);
      this.emit('error', { message: '마이크 접근 권한이 필요합니다.' });
      throw error;
    }
  }

  // 청취자 전용 초기화 (배심원/관전자)
  async initializeAsListener(): Promise<void> {
    if (this.isInitialized) return;
    
    console.log('[WebRTC] Initializing as listener');
    
    // WebSocket 이벤트 리스너 등록
    this.setupWebSocketListeners();
    
    this.isInitialized = true;
    
    // 약간의 딜레이 후 참가자 목록 요청 (서버 연결 확인)
    setTimeout(() => {
      this.requestParticipants();
    }, 100);
  }

  private setupWebSocketListeners() {
    // 참가자 목록 수신
    const unsubParticipants = wsService.on('ROOM_PARTICIPANTS', (data: {
      participants: Participant[];
      myConnectionId: string;
    }) => {
      console.log('[WebRTC] Room participants:', data);
      this.myConnectionId = data.myConnectionId;
      this.participants = data.participants;
      
      // 자신보다 먼저 들어온 참가자들에게 offer 전송
      this.connectToExistingPeers();
    });

    // Offer 수신 (다른 피어가 연결 요청)
    const unsubOffer = wsService.on('WEBRTC_OFFER', async (data: {
      fromConnectionId: string;
      offer: RTCSessionDescriptionInit;
    }) => {
      console.log('[WebRTC] Received offer from:', data.fromConnectionId);
      await this.handleOffer(data.fromConnectionId, data.offer);
    });

    // Answer 수신
    const unsubAnswer = wsService.on('WEBRTC_ANSWER', async (data: {
      fromConnectionId: string;
      answer: RTCSessionDescriptionInit;
    }) => {
      console.log('[WebRTC] Received answer from:', data.fromConnectionId);
      await this.handleAnswer(data.fromConnectionId, data.answer);
    });

    // ICE Candidate 수신
    const unsubCandidate = wsService.on('WEBRTC_CANDIDATE', async (data: {
      fromConnectionId: string;
      candidate: RTCIceCandidateInit;
    }) => {
      await this.handleCandidate(data.fromConnectionId, data.candidate);
    });
    
    // Offer 요청 수신 (청취자가 스트림을 요청)
    const unsubRequestOffer = wsService.on('WEBRTC_REQUEST_OFFER', async (data: {
      fromConnectionId: string;
    }) => {
      console.log('[WebRTC] Received request for offer from:', data.fromConnectionId);
      // 로컬 스트림이 있을 때만 (토론자) offer 전송
      if (this.localStream && !this.peerConnections.has(data.fromConnectionId)) {
        await this.createOffer(data.fromConnectionId);
      }
    });

    this.unsubscribers.push(unsubParticipants, unsubOffer, unsubAnswer, unsubCandidate, unsubRequestOffer);
  }

  // 방 참가자 목록 요청
  requestParticipants() {
    wsService.send({ action: 'GET_ROOM_PARTICIPANTS' });
  }

  // 기존 참가자들에게 연결
  private async connectToExistingPeers() {
    const myRole = this.getMyRole();
    console.log('[WebRTC] Connecting to peers, my role:', myRole, 'localStream:', !!this.localStream);
    
    for (const participant of this.participants) {
      if (participant.connectionId === this.myConnectionId) continue;
      if (this.peerConnections.has(participant.connectionId)) continue;

      // 로컬 스트림이 있으면 (토론자) 모든 참가자에게 offer
      if (this.localStream) {
        console.log('[WebRTC] I am debater, sending offer to:', participant.connectionId);
        await this.createOffer(participant.connectionId);
      }
      // 청취자는 offer를 보내지 않음 - 토론자가 offer를 보내길 기다림
      // 대신 토론자에게 "나 왔어요" 시그널 전송
      else if (participant.role === 'debater') {
        console.log('[WebRTC] I am listener, requesting stream from debater:', participant.connectionId);
        wsService.send({
          action: 'WEBRTC_REQUEST_OFFER',
          targetConnectionId: participant.connectionId,
        });
      }
    }
  }
  
  // 내 역할 확인
  private getMyRole(): string {
    const me = this.participants.find(p => p.connectionId === this.myConnectionId);
    return me?.role || 'unknown';
  }

  // PeerConnection 생성
  private createPeerConnection(remoteConnectionId: string): RTCPeerConnection {
    const pc = new RTCPeerConnection(ICE_SERVERS);

    // ICE candidate 이벤트
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log(`[WebRTC] Sending ICE candidate to ${remoteConnectionId}`);
        wsService.send({
          action: 'WEBRTC_CANDIDATE',
          targetConnectionId: remoteConnectionId,
          candidate: event.candidate.toJSON(),
        });
      }
    };

    // ICE 연결 상태 변경
    pc.oniceconnectionstatechange = () => {
      console.log(`[WebRTC] ICE state with ${remoteConnectionId}:`, pc.iceConnectionState);
    };

    // 연결 상태 변경
    pc.onconnectionstatechange = () => {
      console.log(`[WebRTC] Connection state with ${remoteConnectionId}:`, pc.connectionState);
      if (pc.connectionState === 'connected') {
        this.emit('peerConnected', { connectionId: remoteConnectionId });
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        this.emit('peerDisconnected', { connectionId: remoteConnectionId });
        this.removePeerConnection(remoteConnectionId);
      }
    };

    // 원격 스트림 수신
    pc.ontrack = (event) => {
      console.log('[WebRTC] Remote track received from:', remoteConnectionId, event.track.kind);
      const [remoteStream] = event.streams;
      
      if (!remoteStream) {
        console.warn('[WebRTC] No remote stream in track event');
        return;
      }
      
      // 오디오 요소 생성 및 재생
      const audio = new Audio();
      audio.srcObject = remoteStream;
      audio.autoplay = true;
      audio.volume = 1.0;
      
      // 재생 시도
      audio.play()
        .then(() => console.log('[WebRTC] Audio playing for:', remoteConnectionId))
        .catch(err => {
          console.warn('[WebRTC] Audio play failed (autoplay policy):', err);
          // 사용자 인터랙션 후 재생 재시도 필요할 수 있음
        });
      
      const peerConn = this.peerConnections.get(remoteConnectionId);
      if (peerConn) {
        peerConn.audioElement = audio;
      }

      this.emit('remoteStreamAdded', { 
        connectionId: remoteConnectionId, 
        stream: remoteStream 
      });
    };

    // 로컬 스트림 추가 (토론자인 경우에만 - 스트림이 있을 때)
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        console.log('[WebRTC] Adding local track:', track.kind);
        pc.addTrack(track, this.localStream!);
      });
    } else {
      // 청취자: recvonly transceiver 추가 (오디오 수신 전용)
      // 이렇게 하면 빈 SDP가 아닌 제대로 된 SDP가 생성됨
      pc.addTransceiver('audio', { direction: 'recvonly' });
      console.log('[WebRTC] Added recvonly audio transceiver for listener');
    }

    this.peerConnections.set(remoteConnectionId, { 
      pc, 
      connectionId: remoteConnectionId,
      iceCandidateQueue: [],
      remoteDescriptionSet: false,
    });
    
    // 이 peer에 대해 미리 도착한 candidates가 있으면 큐에 추가
    const pendingCands = this.pendingCandidates.get(remoteConnectionId);
    if (pendingCands && pendingCands.length > 0) {
      console.log(`[WebRTC] Found ${pendingCands.length} pending candidates for ${remoteConnectionId}`);
      const peerConn = this.peerConnections.get(remoteConnectionId)!;
      peerConn.iceCandidateQueue.push(...pendingCands);
      this.pendingCandidates.delete(remoteConnectionId);
    }
    
    return pc;
  }

  // Offer 생성 및 전송
  private async createOffer(targetConnectionId: string) {
    const pc = this.createPeerConnection(targetConnectionId);

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      wsService.send({
        action: 'WEBRTC_OFFER',
        targetConnectionId,
        offer: pc.localDescription!,
      });

      console.log('[WebRTC] Sent offer to:', targetConnectionId);
    } catch (error) {
      console.error('[WebRTC] Failed to create offer:', error);
    }
  }

  // Offer 처리 및 Answer 전송
  private async handleOffer(fromConnectionId: string, offer: RTCSessionDescriptionInit) {
    let peerConn = this.peerConnections.get(fromConnectionId);
    let pc: RTCPeerConnection;
    
    if (!peerConn) {
      pc = this.createPeerConnection(fromConnectionId);
      peerConn = this.peerConnections.get(fromConnectionId)!;
    } else {
      pc = peerConn.pc;
    }

    try {
      console.log('[WebRTC] Setting remote description (offer) from:', fromConnectionId);
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      peerConn.remoteDescriptionSet = true;
      
      // 버퍼된 ICE candidates 처리
      await this.flushIceCandidateQueue(fromConnectionId);
      
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      wsService.send({
        action: 'WEBRTC_ANSWER',
        targetConnectionId: fromConnectionId,
        answer: pc.localDescription!,
      });

      console.log('[WebRTC] Sent answer to:', fromConnectionId);
    } catch (error) {
      console.error('[WebRTC] Failed to handle offer:', error);
    }
  }

  // Answer 처리
  private async handleAnswer(fromConnectionId: string, answer: RTCSessionDescriptionInit) {
    const peerConn = this.peerConnections.get(fromConnectionId);
    if (!peerConn) {
      console.warn('[WebRTC] No peer connection for answer from:', fromConnectionId);
      return;
    }

    try {
      console.log('[WebRTC] Setting remote description (answer) from:', fromConnectionId);
      await peerConn.pc.setRemoteDescription(new RTCSessionDescription(answer));
      peerConn.remoteDescriptionSet = true;
      
      // 버퍼된 ICE candidates 처리
      await this.flushIceCandidateQueue(fromConnectionId);
      
      console.log('[WebRTC] Answer applied from:', fromConnectionId);
    } catch (error) {
      console.error('[WebRTC] Failed to handle answer:', error);
    }
  }

  // 버퍼된 ICE candidates 처리
  private async flushIceCandidateQueue(connectionId: string) {
    const peerConn = this.peerConnections.get(connectionId);
    if (!peerConn) return;
    
    const queue = peerConn.iceCandidateQueue;
    if (queue.length === 0) return;
    
    console.log(`[WebRTC] Flushing ${queue.length} buffered ICE candidates for ${connectionId}`);
    
    for (const candidate of queue) {
      try {
        await peerConn.pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (error) {
        console.error('[WebRTC] Failed to add buffered ICE candidate:', error);
      }
    }
    
    peerConn.iceCandidateQueue = [];
  }

  // ICE Candidate 처리
  private async handleCandidate(fromConnectionId: string, candidate: RTCIceCandidateInit) {
    const peerConn = this.peerConnections.get(fromConnectionId);
    
    // PeerConnection이 아직 없으면 pending에 저장
    if (!peerConn) {
      console.log(`[WebRTC] Buffering ICE candidate (no peer conn yet) from: ${fromConnectionId}`);
      if (!this.pendingCandidates.has(fromConnectionId)) {
        this.pendingCandidates.set(fromConnectionId, []);
      }
      this.pendingCandidates.get(fromConnectionId)!.push(candidate);
      return;
    }
    
    // Remote description이 설정되지 않았으면 큐에 저장
    if (!peerConn.remoteDescriptionSet) {
      console.log(`[WebRTC] Buffering ICE candidate (no remote desc) from: ${fromConnectionId}`);
      peerConn.iceCandidateQueue.push(candidate);
      return;
    }

    try {
      await peerConn.pc.addIceCandidate(new RTCIceCandidate(candidate));
      console.log(`[WebRTC] Added ICE candidate from: ${fromConnectionId}`);
    } catch (error) {
      console.error('[WebRTC] Failed to add ICE candidate:', error);
    }
  }

  // PeerConnection 제거
  private removePeerConnection(connectionId: string) {
    const peerConn = this.peerConnections.get(connectionId);
    if (peerConn) {
      peerConn.pc.close();
      if (peerConn.audioElement) {
        peerConn.audioElement.pause();
        peerConn.audioElement.srcObject = null;
      }
      this.peerConnections.delete(connectionId);
    }
  }

  // 음소거 설정
  setMuted(muted: boolean) {
    this.isMuted = muted;
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = !muted;
      });
    }
    console.log('[WebRTC] Muted:', muted);
  }

  // 음소거 상태 확인
  getMuted(): boolean {
    return this.isMuted;
  }

  // 현재 연결 수
  getConnectionCount(): number {
    return this.peerConnections.size;
  }

  // 정리
  cleanup() {
    // 모든 피어 연결 종료
    this.peerConnections.forEach((_, connectionId) => {
      this.removePeerConnection(connectionId);
    });

    // 로컬 스트림 종료
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    // WebSocket 리스너 정리
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];

    // 이벤트 리스너 정리
    this.eventListeners.clear();
    
    // pending candidates 정리
    this.pendingCandidates.clear();

    this.isInitialized = false;
    this.myConnectionId = null;
    this.participants = [];

    console.log('[WebRTC] Cleaned up');
  }
}

export const webrtcService = new WebRTCService();
